/**
 * ActivityPage.js — Unified "you need to look at this" inbox.
 *
 * Phase 1 of the Notifications + AI Alerts + Tasks consolidation. Collapses
 * three previously separate sidebar destinations into one tabbed surface so
 * the user clears a single unread badge instead of chasing three.
 *
 * Architecture: this page is a thin shell — each tab embeds the existing
 * page component (NotificationsPage, AIAlertsPage) or, for tasks, a small
 * read-only list that links out to CRM for full editing. Zero changes to
 * the underlying IPC handlers, zero schema risk. Phase 2 (table merge) is
 * a separate migration and can ship independently.
 *
 * Why read-only tasks here? Keeping task editing in CRM avoids forking the
 * task UI into two places. Activity surfaces what's actionable today;
 * clicking through opens the full task in CRM context where comments,
 * attachments, and recurrence live.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Bell, ShieldAlert, ClipboardList, ExternalLink } from 'lucide-react';
import NotificationsPage from './NotificationsPage';
import AIAlertsPage from './AIAlertsPage';

const API = () => (typeof window !== 'undefined' ? window.zignagenixAPI : null);

const TABS = [
  { id: 'notifications', label: 'Notifications', icon: Bell,          countKey: 'notifications', perm: 'notifications:read' },
  { id: 'alerts',        label: 'AI Alerts',     icon: ShieldAlert,   countKey: 'alerts',        perm: 'alerts:read' },
  { id: 'tasks',         label: 'My Tasks',      icon: ClipboardList, countKey: 'tasks',         perm: 'crm:read' },
];

export default function ActivityPage({ user, onNavigate, initialTab }) {
  const perms = Array.isArray(user?.permissions) ? user.permissions : [];
  const visibleTabs = TABS.filter((t) => perms.includes(t.perm));

  // Pick the first allowed tab as the default; honor `initialTab` from the
  // legacy /notifications and /aialerts routes so bookmarks keep working.
  const defaultTab = visibleTabs.find((t) => t.id === initialTab)?.id
    || visibleTabs[0]?.id
    || 'notifications';

  const [activeTab, setActiveTab] = useState(defaultTab);
  const [counts, setCounts] = useState({ notifications: 0, alerts: 0, tasks: 0, total: 0 });

  const refreshCounts = useCallback(async () => {
    const api = API();
    if (!api?.getActivityUnread) return;
    try {
      const r = await api.getActivityUnread();
      if (r && typeof r === 'object' && !r.error) {
        setCounts({
          notifications: r.notifications || 0,
          alerts: r.alerts || 0,
          tasks: r.tasks || 0,
          total: r.total || 0,
        });
      }
    } catch (err) {
      // Soft fail — keep last known counts. Don't surface as an error to
      // the user; they came here to look at the inbox, not the badge.
      console.warn('[Activity] unread roll-up failed:', err?.message || err);
    }
  }, []);

  useEffect(() => {
    refreshCounts();
    // Re-poll every 60s while the page is visible. The sidebar badge has
    // its own poller, but this keeps the tab counts honest after the user
    // marks something read inside a tab.
    let interval = null;
    const onVisibility = () => {
      if (document.hidden) {
        if (interval) clearInterval(interval);
        interval = null;
      } else {
        refreshCounts();
        interval = setInterval(refreshCounts, 60000);
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    interval = setInterval(refreshCounts, 60000);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      if (interval) clearInterval(interval);
    };
  }, [refreshCounts]);

  if (visibleTabs.length === 0) {
    return (
      <div className="page">
        <h1 className="page-title">Activity</h1>
        <div className="empty-state" style={{ padding: 48, textAlign: 'center', color: '#9ca3af' }}>
          <Bell size={48} />
          <p>You don't have permission to view notifications, alerts, or tasks.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page activity-page">
      <div className="activity-header">
        <h1 className="page-title">Activity</h1>
        {counts.total > 0 && (
          <div className="activity-total-badge" title="Items waiting for your attention">
            {counts.total} waiting
          </div>
        )}
      </div>

      <div className="activity-tabs" role="tablist" aria-label="Activity sections">
        {visibleTabs.map((tab) => {
          const Icon = tab.icon;
          const count = counts[tab.countKey] || 0;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              role="tab"
              aria-selected={isActive}
              aria-controls={`activity-panel-${tab.id}`}
              className={`activity-tab ${isActive ? 'activity-tab--active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <Icon size={18} strokeWidth={isActive ? 2.2 : 1.8} />
              <span>{tab.label}</span>
              {count > 0 && (
                <span className="activity-tab-count">{count}</span>
              )}
            </button>
          );
        })}
      </div>

      <div
        className="activity-panel"
        id={`activity-panel-${activeTab}`}
        role="tabpanel"
      >
        {activeTab === 'notifications' && (
          <div className="activity-embedded">
            <NotificationsPage user={user} onNavigate={onNavigate} />
          </div>
        )}
        {activeTab === 'alerts' && (
          <div className="activity-embedded">
            <AIAlertsPage user={user} onNavigate={onNavigate} />
          </div>
        )}
        {activeTab === 'tasks' && (
          <MyTasksTab user={user} onNavigate={onNavigate} onChange={refreshCounts} />
        )}
      </div>

      <style jsx>{`
        .activity-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
        }
        .activity-total-badge {
          background: #DC3545;
          color: white;
          padding: 6px 14px;
          border-radius: 20px;
          font-size: 13px;
          font-weight: 600;
        }
        .activity-tabs {
          display: flex;
          gap: 4px;
          border-bottom: 1px solid #E8ECF1;
          margin-bottom: 16px;
          overflow-x: auto;
        }
        .activity-tab {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 16px;
          border: none;
          background: transparent;
          color: #6C757D;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          border-bottom: 2px solid transparent;
          margin-bottom: -1px;
          white-space: nowrap;
          transition: color 0.15s, border-color 0.15s;
        }
        .activity-tab:hover {
          color: #2E8B98;
        }
        .activity-tab--active {
          color: #2E8B98;
          border-bottom-color: #2E8B98;
          font-weight: 600;
        }
        .activity-tab-count {
          background: #2E8B98;
          color: white;
          padding: 2px 8px;
          border-radius: 10px;
          font-size: 11px;
          font-weight: 700;
          min-width: 20px;
          text-align: center;
        }
        .activity-tab:not(.activity-tab--active) .activity-tab-count {
          background: #DC3545;
        }
        /* Hide the embedded child page's outer .page padding so the tab
           content lines up with the activity-page container instead of
           getting double-padded. */
        .activity-embedded :global(.page) {
          padding: 0;
          background: transparent;
        }
        .activity-embedded :global(.page-title) {
          display: none;
        }
      `}</style>
    </div>
  );
}

// ─── My Tasks tab (read-only view that links out to CRM) ──────────────────
//
// Intentionally minimal. The CRM tab is the canonical task editor — this
// surface answers "what do I need to do today?" and links each row through
// to the CRM with the task ID so the user gets the full edit affordances
// when they're ready to act on it.
function MyTasksTab({ user, onNavigate, onChange }) {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    const api = API();
    if (!api?.getCRMTasks) {
      setError('CRM tasks API unavailable.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      // myTasks=true filters to assigned_to = session.userId server-side;
      // status pending/in_progress excludes completed and cancelled.
      const r = await api.getCRMTasks({ myTasks: true, status: 'pending' });
      const list = Array.isArray(r) ? r : (Array.isArray(r?.data) ? r.data : []);
      setTasks(list);
    } catch (err) {
      setError(err?.message || 'Failed to load tasks.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleOpenInCRM = (task) => {
    if (typeof onNavigate === 'function') {
      // Hand control back to App.js's setActivePage. We don't have a
      // deep-link router so we just navigate to crm; the user lands on
      // their My Day surface which already highlights overdue tasks.
      onNavigate('crm');
    }
  };

  const handleComplete = async (taskId) => {
    const api = API();
    if (!api?.completeCRMTask) return;
    try {
      await api.completeCRMTask(taskId);
      setTasks((prev) => prev.filter((t) => t.id !== taskId));
      if (typeof onChange === 'function') onChange();
    } catch (err) {
      console.warn('[Activity/Tasks] complete failed:', err?.message || err);
    }
  };

  const fmtDue = (date) => {
    if (!date) return null;
    const d = new Date(date);
    if (Number.isNaN(d.getTime())) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dueDay = new Date(d);
    dueDay.setHours(0, 0, 0, 0);
    const diffDays = Math.round((dueDay - today) / 86400000);
    if (diffDays < 0)  return { text: `${Math.abs(diffDays)}d overdue`, overdue: true };
    if (diffDays === 0) return { text: 'Due today', overdue: false };
    if (diffDays === 1) return { text: 'Due tomorrow', overdue: false };
    if (diffDays < 7)  return { text: `Due in ${diffDays}d`, overdue: false };
    return { text: d.toLocaleDateString(), overdue: false };
  };

  return (
    <div className="tasks-tab">
      {loading && <p className="loading-text">Loading tasks...</p>}
      {error && <p className="error-text">{error}</p>}
      {!loading && !error && tasks.length === 0 && (
        <div className="empty-state">
          <ClipboardList size={48} />
          <p>Nothing on your plate today. Open tasks live in the CRM.</p>
          <button className="btn-ghost btn-sm" onClick={() => onNavigate?.('crm')}>
            Open CRM
          </button>
        </div>
      )}

      {!loading && tasks.length > 0 && (
        <div className="tasks-list">
          {tasks.map((task) => {
            const due = fmtDue(task.due_date);
            const contactLabel = task.contact_first || task.contact_last
              ? `${task.contact_first || ''} ${task.contact_last || ''}`.trim()
              : (task.patient_first || task.patient_last
                  ? `${task.patient_first || ''} ${task.patient_last || ''}`.trim()
                  : null);
            return (
              <div key={task.id} className={`task-row ${task.priority === 'urgent' ? 'task-row--urgent' : ''}`}>
                <div className="task-checkbox">
                  <input
                    type="checkbox"
                    onChange={() => handleComplete(task.id)}
                    aria-label={`Mark "${task.title}" complete`}
                  />
                </div>
                <div className="task-content">
                  <div className="task-title">{task.title}</div>
                  {contactLabel && (
                    <div className="task-contact">{contactLabel}</div>
                  )}
                </div>
                <div className="task-meta">
                  {task.priority && task.priority !== 'normal' && (
                    <span className={`task-priority task-priority--${task.priority}`}>
                      {task.priority}
                    </span>
                  )}
                  {due && (
                    <span className={`task-due ${due.overdue ? 'task-due--overdue' : ''}`}>
                      {due.text}
                    </span>
                  )}
                  <button
                    className="task-open"
                    onClick={() => handleOpenInCRM(task)}
                    title="Open in CRM"
                  >
                    <ExternalLink size={14} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <style jsx>{`
        .tasks-tab { padding: 8px 0; }
        .tasks-list {
          background: #FFFFFF;
          border: 1px solid #E8ECF1;
          border-radius: 12px;
          overflow: hidden;
        }
        .task-row {
          display: grid;
          grid-template-columns: auto 1fr auto;
          gap: 12px;
          align-items: center;
          padding: 12px 16px;
          border-bottom: 1px solid #E8ECF1;
        }
        .task-row:last-child { border-bottom: none; }
        .task-row:hover { background: #F8F9FA; }
        .task-row--urgent { border-left: 3px solid #DC3545; padding-left: 13px; }
        .task-title { font-weight: 600; color: #1f2937; }
        .task-contact { font-size: 12px; color: #6C757D; margin-top: 2px; }
        .task-meta { display: flex; gap: 8px; align-items: center; }
        .task-priority {
          padding: 2px 8px; border-radius: 10px; font-size: 11px; font-weight: 600;
          background: #FEF3C7; color: #92400E; text-transform: uppercase;
        }
        .task-priority--urgent { background: #FEE2E2; color: #991B1B; }
        .task-priority--high { background: #FEF3C7; color: #92400E; }
        .task-due {
          font-size: 12px; color: #6C757D;
        }
        .task-due--overdue { color: #DC3545; font-weight: 600; }
        .task-open {
          background: transparent; border: none; cursor: pointer;
          color: #6C757D; padding: 4px; border-radius: 4px;
        }
        .task-open:hover { color: #2E8B98; background: #f0f9ff; }
        .empty-state {
          text-align: center; padding: 48px 24px; color: #9ca3af;
        }
        .empty-state svg { color: #d1d5db; margin-bottom: 16px; }
        .loading-text { padding: 24px; text-align: center; color: #6C757D; }
        .error-text { padding: 24px; text-align: center; color: #DC3545; }
      `}</style>
    </div>
  );
}
